import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users2, ClipboardCheck, GraduationCap, Users, Bell, User, LogOut, Lock, ChevronDown, Menu, Wifi, Calendar, MessageCircle, FileBarChart, Settings, BookOpen, School, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSchoolProfile } from '../hooks/useSchoolProfile';
import client, { resolveUrl } from '../api/client';
import PlatformAnnouncementBanner from './PlatformAnnouncementBanner';

/* Sidebar items */
const SIDEBAR_ITEMS = [
  { to: '/teacher',                Icon: Home,          label: 'Home',                exact: true },
  { to: '/teacher/attendance',     Icon: BookOpen,       label: 'My Classes'                       },
  { to: '/teacher/students',       Icon: Users,          label: 'Students'                          },
  { to: '/teacher/attendance',     Icon: ClipboardCheck, label: 'Attendance'                        },
  { to: '/teacher/grades',         Icon: GraduationCap,  label: 'Gradebook'                         },
  { to: '/teacher/grade-requests', Icon: ClipboardList,  label: 'Grade Requests'                    },
  { to: '/teacher/homework',       Icon: FileBarChart,   label: 'Homework/Assignment'               },
  { to: '/teacher/messages',       Icon: MessageCircle,  label: 'Messages'                          },
  { to: '/teacher/timetable',      Icon: Calendar,       label: 'Timetable'                         },
  { to: '/teacher/report',         Icon: FileBarChart,   label: 'Reports'                           },
  { to: '/teacher/profile',        Icon: Settings,       label: 'Settings'                          },
];

/* Bottom tab bar items (mobile) */
const BOTTOM_TABS = [
  { to: '/teacher',            Icon: Home,          label: 'Home',      exact: true },
  { to: '/teacher/attendance', Icon: ClipboardCheck, label: 'Attendance'            },
  { to: '/teacher/grades',     Icon: GraduationCap,  label: 'Grades'                },
  { to: '/teacher/homework',   Icon: FileBarChart,   label: 'Homework/Assignment' },
  { to: '/teacher/alerts',     Icon: Bell,           label: 'Alerts'                },
];

/* All nav items for mobile drawer */
const DRAWER_ITEMS = [
  { to: '/teacher',                Icon: Home,          label: 'Home',          exact: true },
  { to: '/teacher/attendance',     Icon: BookOpen,       label: 'My Classes'                },
  { to: '/teacher/students',       Icon: Users,          label: 'Students'                  },
  { to: '/teacher/attendance',     Icon: ClipboardCheck, label: 'Attendance'                },
  { to: '/teacher/grades',         Icon: GraduationCap,  label: 'Gradebook'                 },
  { to: '/teacher/grade-requests', Icon: ClipboardList,  label: 'Grade Requests'            },
  { to: '/teacher/homework',       Icon: FileBarChart,   label: 'Homework/Assignment'       },
  { to: '/teacher/messages',       Icon: MessageCircle,  label: 'Messages'                  },
  { to: '/teacher/timetable',      Icon: Calendar,       label: 'Timetable'                 },
  { to: '/teacher/report',         Icon: FileBarChart,   label: 'Reports'                   },
  { to: '/teacher/alerts',         Icon: Bell,           label: 'Alerts'                    },
  { to: '/teacher/profile',        Icon: Settings,       label: 'Settings'                  },
];

function getInitials(name) { return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function getGreeting() { const h = new Date().getHours(); if (h < 12) return 'Good morning'; if (h < 17) return 'Good afternoon'; return 'Good evening'; }
function formatDate() { return new Date().toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

/* ─── School Logo or Icon ─── */
function SchoolLogo({ profile, size = 36, color = '#888' }) {
  const logoSrc = profile?.logo_url || profile?.logo;
  if (logoSrc) {
    const src = (logoSrc.startsWith('http') || logoSrc.startsWith('data:'))
      ? logoSrc
      : resolveUrl(logoSrc);
    return (
      <img
        src={src}
        alt="School logo"
        style={{ width: size, height: size, borderRadius: size * 0.28, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0 }}
        onError={e => { e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.28, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '2px solid rgba(255,255,255,0.25)' }}>
      <School size={size * 0.52} color="#fff" />
    </div>
  );
}

/* ─── Profile Dropdown ─── */
function ProfileDropdown({ open, onClose, anchorRef, accentColor }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && anchorRef.current && !anchorRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return (
    <div ref={ref} style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #F0F0EB', width: 280, zIndex: 1001, overflow: 'hidden' }}>
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #F5F5F0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 24, background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{getInitials(user?.full_name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{user?.full_name}</div>
            <div style={{ fontSize: 13, color: '#888' }}>{user?.phone}</div>
            <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#D1FAE5', color: '#059669', marginTop: 4, textTransform: 'capitalize' }}>{user?.role}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: '8px' }}>
        <button onClick={() => { onClose(); navigate('/teacher/profile'); }} style={dropBtn}><User size={16} color="#666" /> My Profile</button>
        <button onClick={() => { onClose(); navigate('/change-password'); }} style={dropBtn}><Lock size={16} color="#666" /> Change Password</button>
        <div style={{ height: 1, background: '#F5F5F0', margin: '4px 0' }} />
        <button onClick={() => { onClose(); logout(); navigate('/login'); }} style={{ ...dropBtn, color: '#DC2626' }}><LogOut size={16} color="#DC2626" /> Log Out</button>
      </div>
    </div>
  );
}

const dropBtn = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', padding: '10px 12px', fontSize: 14, fontWeight: 500, color: '#333', cursor: 'pointer', borderRadius: 10, textAlign: 'left', transition: 'background .12s' };

const TeacherFrameCtx = React.createContext(false);

function TeacherPhoneFrameInner({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { profile, academicYear } = useSchoolProfile();
  const isMobile = useIsMobile();
  const [profileOpen, setProfileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const profileRef = useRef(null);

  // Live unread notification count
  const [unreadCount, setUnreadCount] = useState(0);
  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await client.get('/teacher/notifications/unread-count');
      setUnreadCount(data.count || 0);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    fetchUnread();
    // Poll every 15 seconds for near-real-time badge updates
    const t = setInterval(fetchUnread, 15000);
    return () => clearInterval(t);
  }, [fetchUnread]);
  // Re-fetch when navigating away from notifications page (user may have read some)
  useEffect(() => {
    if (location.pathname !== '/teacher/notifications') fetchUnread();
  }, [location.pathname, fetchUnread]);

  // Grade requests requiring teacher attention (rejected or needs_revision)
  const [gradeReqCount, setGradeReqCount] = useState(0);
  const fetchGradeReqCount = useCallback(async () => {
    try {
      const { data } = await client.get('/teacher/grade-requests');
      const counts = data.counts || {};
      setGradeReqCount((counts.rejected || 0) + (counts.needs_revision || 0));
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    fetchGradeReqCount();
    const t = setInterval(fetchGradeReqCount, 60000);
    return () => clearInterval(t);
  }, [fetchGradeReqCount]);
  useEffect(() => {
    if (location.pathname === '/teacher/grade-requests') fetchGradeReqCount();
  }, [location.pathname, fetchGradeReqCount]);

  // Unread message count
  const [msgCount, setMsgCount] = useState(0);
  const fetchMsgCount = useCallback(async () => {
    try {
      const { data } = await client.get('/teacher/messages');
      setMsgCount(data?.unread_count || 0);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    fetchMsgCount();
    const t = setInterval(fetchMsgCount, 30000);
    return () => clearInterval(t);
  }, [fetchMsgCount]);
  useEffect(() => {
    if (location.pathname === '/teacher/messages') {
      setMsgCount(0);
    } else {
      fetchMsgCount();
    }
  }, [location.pathname, fetchMsgCount]);
  useEffect(() => {
    const h = () => setMsgCount(p => Math.max(0, p - 1));
    window.addEventListener('teacher-msg-read', h);
    return () => window.removeEventListener('teacher-msg-read', h);
  }, []);

  // All branding values come exclusively from the School document.
  // While profile is loading (null), use a neutral grey so no hardcoded
  // school name or brand colour is ever shown.
  const schoolName  = profile?.name   || '';
  const accentColor = profile?.primary_color || '#4B5563'; // neutral grey until DB responds
  const headerBg    = `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}dd 100%)`;

  const isActive = (to, exact) => exact ? location.pathname === to : location.pathname.startsWith(to) && to !== '/teacher';

  /* ─── MOBILE ─── */
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#F8F8F4', overflow: 'hidden', position: 'fixed', inset: 0 }}>

        {/* ── Mobile Top Header ── */}
        <div style={{ background: headerBg, paddingTop: 'calc(10px + env(safe-area-inset-top))', paddingLeft: 14, paddingRight: 14, paddingBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 50 }}>
          <button onClick={() => setDrawerOpen(true)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Menu size={20} color="#fff" />
          </button>

          {/* Center: logo + school name + role badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center', minWidth: 0, padding: '0 8px' }}>
            <SchoolLogo profile={profile} size={30} color={accentColor} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: -0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{schoolName}</div>
                <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.22)', color: '#fff', borderRadius: 6, padding: '2px 7px', letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0 }}>Teacher</span>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{user?.full_name}</div>
            </div>
          </div>

          <button onClick={() => navigate('/teacher/notifications')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}>
            <Bell size={18} color="#fff" />
            {unreadCount > 0 && (
              <div style={{ position: 'absolute', top: 5, right: 5, minWidth: 16, height: 16, borderRadius: 8, background: '#EF4444', border: '1.5px solid ' + accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', padding: '0 3px' }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </div>
            )}
          </button>
        </div>

        {/* ── Drawer Overlay ── */}
        {drawerOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={() => setDrawerOpen(false)}>
            {/* Backdrop */}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
            {/* Drawer panel */}
            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 270, background: `linear-gradient(180deg, ${accentColor} 0%, ${accentColor}ee 100%)`, display: 'flex', flexDirection: 'column', boxShadow: '4px 0 24px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}>
              {/* Drawer header — school identity */}
              <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {/* School branding row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <SchoolLogo profile={profile} size={40} color={accentColor} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{schoolName}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>Teacher Portal · {academicYear}</div>
                  </div>
                </div>
                {/* Teacher identity row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0 }}>
                    {getInitials(user?.full_name)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{user?.full_name}</div>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.85)', textTransform: 'capitalize' }}>{user?.role}</span>
                  </div>
                </div>
              </div>
              {/* Nav items */}
              <nav style={{ flex: 1, padding: '10px 10px', overflowY: 'auto' }}>
                {DRAWER_ITEMS.map(({ to, Icon, label, exact }) => {
                  const active = isActive(to, exact);
                  const itemBadge = label === 'Messages' ? msgCount : label === 'Grade Requests' ? gradeReqCount : 0;
                  return (
                    <button key={label} onClick={() => { navigate(to); setDrawerOpen(false); }} style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                      padding: '12px 14px', borderRadius: 12, border: 'none',
                      background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                      color: active ? '#fff' : 'rgba(255,255,255,0.65)',
                      fontSize: 14, fontWeight: active ? 700 : 500,
                      cursor: 'pointer', textAlign: 'left', marginBottom: 2,
                      borderLeft: active ? '3px solid rgba(255,255,255,0.8)' : '3px solid transparent',
                    }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <Icon size={18} strokeWidth={active ? 2 : 1.5} />
                        {itemBadge > 0 && (
                          <div style={{ position: 'absolute', top: -5, right: -6, minWidth: 15, height: 15, borderRadius: 8, background: '#EF4444', color: '#fff', fontSize: 8, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: `1.5px solid ${accentColor}` }}>
                            {itemBadge > 99 ? '99+' : itemBadge}
                          </div>
                        )}
                      </div>
                      <span style={{ flex: 1 }}>{label}</span>
                      {itemBadge > 0 && (
                        <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center', lineHeight: '16px' }}>
                          {itemBadge > 99 ? '99+' : itemBadge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
              {/* Drawer footer */}
              <div style={{ padding: '12px 14px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button onClick={() => { navigate('/teacher/profile'); setDrawerOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', borderRadius: 10, border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.65)', fontSize: 14, fontWeight: 500, cursor: 'pointer', marginBottom: 4 }}>
                  <User size={16} /> My Profile
                </button>
                <button onClick={() => { logout(); navigate('/login'); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', borderRadius: 10, border: 'none', background: 'rgba(239,68,68,0.15)', color: '#FCA5A5', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  <LogOut size={16} /> Log Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable content area */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
          {children}
        </div>

        {/* Fixed bottom tab bar */}
        <div style={{ display: 'flex', borderTop: '1px solid #E8E8E4', background: '#fff', paddingTop: 6, paddingLeft: 0, paddingRight: 0, paddingBottom: 'env(safe-area-inset-bottom, 10px)', flexShrink: 0, zIndex: 100 }}>
          {BOTTOM_TABS.map(({ to, Icon, label, exact }) => {
            const active = isActive(to, exact);
            return (
              <button key={to} onClick={() => navigate(to)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', color: active ? accentColor : '#9CA3AF' }}>
                <Icon size={22} strokeWidth={active ? 2.2 : 1.5} />
                <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, marginTop: 2 }}>{label}</span>
              </button>
            );
          })}
          {/* More button to open drawer */}
          <button onClick={() => setDrawerOpen(true)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', color: '#9CA3AF' }}>
            <Menu size={22} strokeWidth={1.5} />
            <span style={{ fontSize: 11, fontWeight: 400, marginTop: 2 }}>More</span>
          </button>
        </div>
      </div>
    );
  }

  const schoolId = user?.school_id;

  /* ─── DESKTOP ─── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#F5F5F0', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <header style={{ background: headerBg, padding: '0 28px', flexShrink: 0, zIndex: 100, height: 64 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1400, margin: '0 auto', height: '100%' }}>

          {/* Left: School logo + name + portal label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SchoolLogo profile={profile} size={40} color={accentColor} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: -0.3, lineHeight: 1.1 }}>{schoolName}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.9)', letterSpacing: 0.8, textTransform: 'uppercase' }}>Teacher Portal</span>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 1, fontWeight: 500 }}>
                {academicYear} · {formatDate()}
              </div>
            </div>
          </div>

          {/* Right: greeting + bell + profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Greeting */}
            <div style={{ textAlign: 'right', display: 'none' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{getGreeting()} 👋</div>
            </div>

            {/* Bell */}
            <button
              onClick={() => navigate('/teacher/notifications')}
              style={{ position: 'relative', cursor: 'pointer', background: 'none', border: 'none', padding: '6px', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Notifications"
            >
              <Bell size={22} color="rgba(255,255,255,0.7)" />
              {unreadCount > 0 && (
                <div style={{ position: 'absolute', top: 0, right: 0, minWidth: 18, height: 18, borderRadius: 9, background: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', border: `2px solid ${accentColor}`, padding: '0 3px', pointerEvents: 'none' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}
            </button>

            {/* Profile button */}
            <div style={{ position: 'relative' }} ref={profileRef}>
              <button onClick={() => setProfileOpen(!profileOpen)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 10px 4px 4px', borderRadius: 14, background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', transition: 'all .15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                <div style={{ width: 38, height: 38, borderRadius: 19, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', border: '2px solid rgba(255,255,255,0.3)' }}>
                  {getInitials(user?.full_name)}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.2 }}>{user?.full_name}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'capitalize' }}>{user?.role}</div>
                </div>
                <ChevronDown size={14} color="rgba(255,255,255,0.5)" style={{ transition: 'transform .2s', transform: profileOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              <ProfileDropdown open={profileOpen} onClose={() => setProfileOpen(false)} anchorRef={profileRef} accentColor={accentColor} />
            </div>
          </div>
        </div>
      </header>

      {/* ── Sidebar + Content ── */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* Left Sidebar */}
        <aside style={{ width: 200, background: '#fff', borderRight: '1px solid #F0F0EB', flexShrink: 0, display: 'flex', flexDirection: 'column', paddingTop: 12, overflowY: 'auto', overflowX: 'hidden' }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 10px' }}>
            {SIDEBAR_ITEMS.map(({ to, Icon, label, exact }) => {
              const active = isActive(to, exact);
              const itemBadge = label === 'Messages' ? msgCount : label === 'Grade Requests' ? gradeReqCount : 0;
              return (
                <button key={label} onClick={() => navigate(to)} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: active ? `${accentColor}14` : 'transparent',
                  color: active ? accentColor : '#555',
                  border: 'none',
                  fontSize: 14, fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  transition: 'all .12s',
                  textAlign: 'left',
                  width: '100%',
                  borderLeft: active ? `3px solid ${accentColor}` : '3px solid transparent',
                }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#F8F8F4'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = active ? `${accentColor}14` : 'transparent'; } }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Icon size={20} strokeWidth={active ? 2 : 1.5} />
                    {itemBadge > 0 && (
                      <div style={{ position: 'absolute', top: -5, right: -6, minWidth: 15, height: 15, borderRadius: 8, background: '#EF4444', color: '#fff', fontSize: 8, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: '1.5px solid #fff' }}>
                        {itemBadge > 99 ? '99+' : itemBadge}
                      </div>
                    )}
                  </div>
                  <span style={{ flex: 1 }}>{label}</span>
                  {itemBadge > 0 && (
                    <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center', lineHeight: '16px' }}>
                      {itemBadge > 99 ? '99+' : itemBadge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content — scrollable */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: '#F8F8F4', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {schoolId && (
            <div style={{ padding: '12px 20px 0' }}>
              <PlatformAnnouncementBanner audience="teachers" schoolId={schoolId} />
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

export default function TeacherPhoneFrame({ children }) {
  const isNested = React.useContext(TeacherFrameCtx);
  if (isNested) return <>{children}</>;
  return (
    <TeacherFrameCtx.Provider value={true}>
      <TeacherPhoneFrameInner>{children}</TeacherPhoneFrameInner>
    </TeacherFrameCtx.Provider>
  );
}
